/**
 * SkillSystem -- Manages skill execution during combat.
 *
 * Reads skill definitions from skill_trees.json via DataLoader.
 * Handles AP/mana costs, cooldowns, targeting, damage, healing,
 * status effects, and tile effects.
 *
 * @module SkillSystem
 */

import eventBus from '../core/EventBus.js';
import dataStore from './DataLoader.js';

/**
 * Targeting type constants (matches skill_trees.json targetType values).
 */
const TARGET_TYPES = {
  SINGLE_ENEMY: 'single_enemy',
  SINGLE_ALLY: 'single_ally',
  AOE_ENEMY: 'aoe_enemy',
  AOE_ALLY: 'aoe_ally',
  ADJACENT: 'adjacent',
  SELF: 'self',
  LINE: 'line',
  TILE: 'tile',
};

class SkillSystem {
  constructor() {
    /**
     * Cooldown tracking: Map<combatantId, Map<skillId, turnsRemaining>>
     * @type {Map<string, Map<string, number>>}
     */
    this._cooldowns = new Map();

    /**
     * Reference to the CombatSystem (set during combat start).
     * @type {object|null}
     */
    this._combatSystem = null;

    /**
     * Reference to TileEffects system.
     * @type {object|null}
     */
    this._tileEffects = null;

    /**
     * Reference to StatusEffects system.
     * @type {object|null}
     */
    this._statusEffects = null;
  }

  /**
   * Initialize the skill system with references to other systems.
   * @param {object} combatSystem
   * @param {object} [tileEffects]
   * @param {object} [statusEffects]
   */
  init(combatSystem, tileEffects = null, statusEffects = null) {
    this._combatSystem = combatSystem;
    this._tileEffects = tileEffects;
    this._statusEffects = statusEffects;
  }

  /**
   * Reset all cooldowns (call at combat start).
   */
  reset() {
    this._cooldowns.clear();
  }

  // -- Skill Lookup -----------------------------------------------

  /**
   * Find a skill definition by its ID across all skill trees.
   * @param {string} skillId
   * @returns {{ skill: object, tree: object }|null}
   */
  findSkill(skillId) {
    const trees = dataStore.getAll('skill_trees') || [];
    for (const tree of trees) {
      const skill = tree.skills.find(s => s.id === skillId);
      if (skill) return { skill, tree };
    }
    return null;
  }

  /**
   * Get all learnable skills for a combatant based on their character's skill trees.
   * @param {object} combatant
   * @returns {object[]} Array of { skill, tree, level } objects
   */
  getAvailableSkills(combatant) {
    const character = combatant.character;
    if (!character) return [];

    const results = [];
    const treesToCheck = character.skillTrees || [];

    for (const treeId of treesToCheck) {
      const tree = dataStore.getById('skill_trees', treeId);
      if (!tree) continue;

      for (const skill of tree.skills) {
        const level = character.skills.get(skill.id) || 0;
        if (level > 0 && !skill.isPassive) {
          results.push({ skill, tree, level });
        }
      }
    }

    return results;
  }

  // -- Cooldown Management ----------------------------------------

  /**
   * Get remaining cooldown for a skill on a combatant.
   * @param {string} combatantId
   * @param {string} skillId
   * @returns {number} Turns remaining (0 = ready)
   */
  getCooldown(combatantId, skillId) {
    const map = this._cooldowns.get(combatantId);
    if (!map) return 0;
    return map.get(skillId) || 0;
  }

  /**
   * Set a cooldown for a skill on a combatant.
   * @param {string} combatantId
   * @param {string} skillId
   * @param {number} turns
   */
  _setCooldown(combatantId, skillId, turns) {
    if (!this._cooldowns.has(combatantId)) {
      this._cooldowns.set(combatantId, new Map());
    }
    this._cooldowns.get(combatantId).set(skillId, turns);
  }

  /**
   * Tick all cooldowns for a combatant (call at start of their turn).
   * @param {string} combatantId
   */
  tickCooldowns(combatantId) {
    const map = this._cooldowns.get(combatantId);
    if (!map) return;

    for (const [skillId, remaining] of map) {
      if (remaining > 0) {
        map.set(skillId, remaining - 1);
      }
      if (map.get(skillId) <= 0) {
        map.delete(skillId);
      }
    }
  }

  // -- Validation -------------------------------------------------

  /**
   * Check whether a combatant can use a specific skill.
   * @param {object} combatant
   * @param {string} skillId
   * @returns {{ canUse: boolean, reason: string }}
   */
  canUseSkill(combatant, skillId) {
    if (!combatant.alive) {
      return { canUse: false, reason: 'Combatant is dead.' };
    }

    const found = this.findSkill(skillId);
    if (!found) {
      return { canUse: false, reason: 'Skill not found.' };
    }

    const { skill } = found;

    // Check the character has learned this skill
    const character = combatant.character;
    if (character) {
      const level = character.skills.get(skillId) || 0;
      if (level <= 0) {
        return { canUse: false, reason: 'Skill not learned.' };
      }
    }

    // Passive skills cannot be "used"
    if (skill.isPassive) {
      return { canUse: false, reason: 'Passive skills cannot be activated.' };
    }

    // AP check
    if (combatant.ap < skill.apCost) {
      return { canUse: false, reason: `Not enough AP (need ${skill.apCost}, have ${combatant.ap}).` };
    }

    // Mana check
    const level = character ? (character.skills.get(skillId) || 1) : 1;
    const manaCost = this._getManaCost(skill, level);
    if (combatant.mana < manaCost) {
      return { canUse: false, reason: `Not enough mana (need ${manaCost}, have ${combatant.mana}).` };
    }

    // Cooldown check
    const cd = this.getCooldown(combatant.id, skillId);
    if (cd > 0) {
      return { canUse: false, reason: `On cooldown (${cd} turns remaining).` };
    }

    return { canUse: true, reason: '' };
  }

  /**
   * Get the mana cost for a skill at a given level.
   * @param {object} skill
   * @param {number} level - 1-based
   * @returns {number}
   */
  _getManaCost(skill, level) {
    if (Array.isArray(skill.manaCost)) {
      return skill.manaCost[Math.min(level - 1, skill.manaCost.length - 1)] || 0;
    }
    return skill.manaCost || 0;
  }

  /**
   * Get the damage multiplier for a skill at a given level.
   * @param {object} skill
   * @param {number} level - 1-based
   * @returns {number}
   */
  _getDamageMultiplier(skill, level) {
    if (Array.isArray(skill.damageMultiplier)) {
      return skill.damageMultiplier[Math.min(level - 1, skill.damageMultiplier.length - 1)] || 1.0;
    }
    return skill.damageMultiplier || 1.0;
  }

  // -- Targeting --------------------------------------------------

  /**
   * Get valid target tiles/combatants for a skill from a given combatant.
   * @param {object} combatant - The caster
   * @param {string} skillId
   * @returns {Array<{ x: number, y: number, combatant: object|null }>}
   */
  getSkillTargets(combatant, skillId) {
    const found = this.findSkill(skillId);
    if (!found) return [];

    const { skill } = found;
    const cs = this._combatSystem;
    if (!cs) return [];

    const targets = [];

    switch (skill.targetType) {
      case TARGET_TYPES.SELF:
        targets.push({ x: combatant.gridX, y: combatant.gridY, combatant });
        break;

      case TARGET_TYPES.SINGLE_ENEMY:
        // All alive enemies within range (default range 6 for spells, 2 for melee)
        for (let r = 0; r < cs.rows; r++) {
          for (let c = 0; c < cs.cols; c++) {
            const occ = cs.grid[r][c];
            if (occ && occ.alive && occ.type !== combatant.type) {
              const dist = Math.abs(c - combatant.gridX) + Math.abs(r - combatant.gridY);
              const range = skill.damageType && skill.damageType !== 'physical' ? 6 : 2;
              if (dist <= range) {
                targets.push({ x: c, y: r, combatant: occ });
              }
            }
          }
        }
        break;

      case TARGET_TYPES.SINGLE_ALLY:
        // All alive allies
        for (let r = 0; r < cs.rows; r++) {
          for (let c = 0; c < cs.cols; c++) {
            const occ = cs.grid[r][c];
            if (occ && occ.alive && occ.type === combatant.type) {
              targets.push({ x: c, y: r, combatant: occ });
            }
          }
        }
        break;

      case TARGET_TYPES.AOE_ENEMY:
        // All alive enemies (AoE hits all)
        for (let r = 0; r < cs.rows; r++) {
          for (let c = 0; c < cs.cols; c++) {
            const occ = cs.grid[r][c];
            if (occ && occ.alive && occ.type !== combatant.type) {
              targets.push({ x: c, y: r, combatant: occ });
            }
          }
        }
        break;

      case TARGET_TYPES.AOE_ALLY:
        // All alive allies (buff/heal all)
        for (let r = 0; r < cs.rows; r++) {
          for (let c = 0; c < cs.cols; c++) {
            const occ = cs.grid[r][c];
            if (occ && occ.alive && occ.type === combatant.type) {
              targets.push({ x: c, y: r, combatant: occ });
            }
          }
        }
        break;

      case TARGET_TYPES.ADJACENT:
        // All adjacent enemies
        {
          const dirs = [
            { dx: 0, dy: -1 }, { dx: 0, dy: 1 },
            { dx: -1, dy: 0 }, { dx: 1, dy: 0 },
          ];
          for (const { dx, dy } of dirs) {
            const nx = combatant.gridX + dx;
            const ny = combatant.gridY + dy;
            if (nx < 0 || nx >= cs.cols || ny < 0 || ny >= cs.rows) continue;
            const occ = cs.grid[ny][nx];
            if (occ && occ.alive && occ.type !== combatant.type) {
              targets.push({ x: nx, y: ny, combatant: occ });
            }
          }
        }
        break;

      case TARGET_TYPES.LINE:
        // Return tiles in cardinal directions up to 4 tiles
        {
          const dirs = [
            { dx: 1, dy: 0 }, { dx: -1, dy: 0 },
            { dx: 0, dy: 1 }, { dx: 0, dy: -1 },
          ];
          for (const { dx, dy } of dirs) {
            for (let i = 1; i <= 4; i++) {
              const nx = combatant.gridX + dx * i;
              const ny = combatant.gridY + dy * i;
              if (nx < 0 || nx >= cs.cols || ny < 0 || ny >= cs.rows) break;
              const occ = cs.grid[ny][nx];
              if (occ && occ.alive && occ.type !== combatant.type) {
                targets.push({ x: nx, y: ny, combatant: occ });
              }
            }
          }
        }
        break;

      case TARGET_TYPES.TILE:
        // Return all walkable tiles within range 4
        for (let r = 0; r < cs.rows; r++) {
          for (let c = 0; c < cs.cols; c++) {
            const dist = Math.abs(c - combatant.gridX) + Math.abs(r - combatant.gridY);
            if (dist > 0 && dist <= 4) {
              targets.push({ x: c, y: r, combatant: cs.grid[r][c] || null });
            }
          }
        }
        break;

      default:
        break;
    }

    return targets;
  }

  // -- Execution --------------------------------------------------

  /**
   * Use a skill from a combatant on a target.
   * @param {object} combatant - The caster
   * @param {string} skillId
   * @param {{ x: number, y: number, combatant: object|null }} target
   * @returns {{ success: boolean, message: string, results: object[] }}
   */
  useSkill(combatant, skillId, target) {
    const check = this.canUseSkill(combatant, skillId);
    if (!check.canUse) {
      return { success: false, message: check.reason, results: [] };
    }

    const found = this.findSkill(skillId);
    const { skill, tree } = found;
    const character = combatant.character;
    const level = character ? (character.skills.get(skillId) || 1) : 1;
    const manaCost = this._getManaCost(skill, level);

    // Deduct AP and mana
    combatant.ap -= skill.apCost;
    combatant.mana -= manaCost;

    // Set cooldown
    if (skill.cooldown > 0) {
      this._setCooldown(combatant.id, skillId, skill.cooldown);
    }

    // Determine actual targets based on skill type
    const actualTargets = this._resolveTargets(combatant, skill, target);

    // Execute skill effects on each target
    const results = [];
    for (const t of actualTargets) {
      const result = this._applySkillToTarget(combatant, skill, level, t);
      results.push(result);
    }

    // Create tile effects for fire/ice/poison AoE skills
    if (this._tileEffects && skill.damageType) {
      this._maybeCreateTileEffect(combatant, skill, target);
    }

    // Build message
    const targetNames = actualTargets
      .filter(t => t.combatant)
      .map(t => t.combatant.name)
      .join(', ');
    const msg = `${combatant.name} uses ${skill.name}${targetNames ? ` on ${targetNames}` : ''}! [${combatant.ap} AP, ${combatant.mana} Mana left]`;

    // Log and emit
    if (this._combatSystem) {
      this._combatSystem._addLog(msg);
    }

    eventBus.emit('combat:skill-use', {
      combatant,
      skill,
      level,
      target,
      results,
      apCost: skill.apCost,
      manaCost,
    });

    return { success: true, message: msg, results };
  }

  /**
   * Resolve the actual set of targets based on the skill's targeting type.
   * For single-target skills, this is just the selected target.
   * For AoE skills, this is all valid targets.
   * @param {object} combatant
   * @param {object} skill
   * @param {{ x: number, y: number, combatant: object|null }} selectedTarget
   * @returns {Array<{ x: number, y: number, combatant: object|null }>}
   */
  _resolveTargets(combatant, skill, selectedTarget) {
    const cs = this._combatSystem;
    if (!cs) return [selectedTarget];

    switch (skill.targetType) {
      case TARGET_TYPES.SELF:
        return [{ x: combatant.gridX, y: combatant.gridY, combatant }];

      case TARGET_TYPES.SINGLE_ENEMY:
      case TARGET_TYPES.SINGLE_ALLY:
        return [selectedTarget];

      case TARGET_TYPES.AOE_ENEMY: {
        // Hit all alive enemies
        const targets = [];
        for (const e of (combatant.type === 'player' ? cs.enemies : cs.players)) {
          if (e.alive) {
            targets.push({ x: e.gridX, y: e.gridY, combatant: e });
          }
        }
        return targets;
      }

      case TARGET_TYPES.AOE_ALLY: {
        const targets = [];
        for (const a of (combatant.type === 'player' ? cs.players : cs.enemies)) {
          if (a.alive) {
            targets.push({ x: a.gridX, y: a.gridY, combatant: a });
          }
        }
        return targets;
      }

      case TARGET_TYPES.ADJACENT: {
        const targets = [];
        const dirs = [
          { dx: 0, dy: -1 }, { dx: 0, dy: 1 },
          { dx: -1, dy: 0 }, { dx: 1, dy: 0 },
        ];
        for (const { dx, dy } of dirs) {
          const nx = combatant.gridX + dx;
          const ny = combatant.gridY + dy;
          if (nx < 0 || nx >= cs.cols || ny < 0 || ny >= cs.rows) continue;
          const occ = cs.grid[ny][nx];
          if (occ && occ.alive && occ.type !== combatant.type) {
            targets.push({ x: nx, y: ny, combatant: occ });
          }
        }
        return targets.length > 0 ? targets : [selectedTarget];
      }

      case TARGET_TYPES.LINE: {
        // Hit all enemies in a line from caster through selected target
        if (!selectedTarget.combatant) return [selectedTarget];
        const dx = Math.sign(selectedTarget.x - combatant.gridX);
        const dy = Math.sign(selectedTarget.y - combatant.gridY);
        const targets = [];
        for (let i = 1; i <= 4; i++) {
          const nx = combatant.gridX + dx * i;
          const ny = combatant.gridY + dy * i;
          if (nx < 0 || nx >= cs.cols || ny < 0 || ny >= cs.rows) break;
          const occ = cs.grid[ny][nx];
          if (occ && occ.alive && occ.type !== combatant.type) {
            targets.push({ x: nx, y: ny, combatant: occ });
          }
        }
        return targets.length > 0 ? targets : [selectedTarget];
      }

      case TARGET_TYPES.TILE:
        return [selectedTarget];

      default:
        return [selectedTarget];
    }
  }

  /**
   * Apply a skill's effects to a single target.
   * @param {object} caster
   * @param {object} skill
   * @param {number} level
   * @param {{ x: number, y: number, combatant: object|null }} target
   * @returns {object} Result with damage, healing, statuses applied
   */
  _applySkillToTarget(caster, skill, level, target) {
    const result = {
      target: target.combatant,
      damage: 0,
      healing: 0,
      statusesApplied: [],
      killed: false,
    };

    const targetUnit = target.combatant;
    if (!targetUnit || !targetUnit.alive) return result;

    // Calculate and apply damage
    if (skill.damageMultiplier && skill.damageType) {
      const multiplier = this._getDamageMultiplier(skill, level);
      const baseStat = skill.damageType === 'physical'
        ? caster.damage + this._getMod(caster.attributes.str)
        : caster.damage + this._getMod(caster.attributes.int);

      let dmg = Math.max(1, Math.floor(baseStat * multiplier));

      // Check exposed status on target (damage amplify)
      const exposed = targetUnit.statusEffects.find(e => e.id === 'exposed');
      if (exposed) {
        dmg = Math.floor(dmg * 1.25);
      }

      // Check defending status on target
      const defending = targetUnit.statusEffects.find(e => e.id === 'defending');
      if (defending) {
        dmg = Math.max(1, Math.floor(dmg * 0.5));
      }

      // Apply damage
      targetUnit.hp -= dmg;
      targetUnit.hp = Math.max(0, targetUnit.hp);
      result.damage = dmg;

      if (targetUnit.hp <= 0) {
        targetUnit.alive = false;
        result.killed = true;
        if (this._combatSystem) {
          this._combatSystem._addLog(`${targetUnit.name} has been defeated by ${skill.name}!`);
        }
        eventBus.emit('combat:kill', { attacker: caster, target: targetUnit });
      } else {
        if (this._combatSystem) {
          this._combatSystem._addLog(`${skill.name} hits ${targetUnit.name} for ${dmg} ${skill.damageType} damage.`);
        }
      }

      eventBus.emit('combat:skill-effect', {
        type: 'damage',
        caster,
        target: targetUnit,
        skill,
        value: dmg,
      });
    }

    // Apply healing (for self/ally target skills with no damage type but a multiplier)
    if (!skill.damageType && skill.damageMultiplier && skill.targetType !== TARGET_TYPES.SELF) {
      const multiplier = this._getDamageMultiplier(skill, level);
      const healBase = caster.damage + this._getMod(caster.attributes.wis);
      const healing = Math.max(1, Math.floor(healBase * multiplier));
      targetUnit.hp = Math.min(targetUnit.maxHp, targetUnit.hp + healing);
      result.healing = healing;

      if (this._combatSystem) {
        this._combatSystem._addLog(`${skill.name} heals ${targetUnit.name} for ${healing} HP.`);
      }

      eventBus.emit('combat:skill-effect', {
        type: 'heal',
        caster,
        target: targetUnit,
        skill,
        value: healing,
      });
    }

    // Apply status effects
    if (skill.statusApply && skill.statusApply.length > 0) {
      for (const statusDef of skill.statusApply) {
        const roll = Math.random();
        if (roll <= statusDef.chance) {
          this._applyStatus(targetUnit, statusDef.statusId, statusDef.duration, caster);
          result.statusesApplied.push(statusDef.statusId);
        }
      }
    }

    return result;
  }

  /**
   * Apply a status effect to a target.
   * @param {object} target
   * @param {string} statusId
   * @param {number} duration
   * @param {object} [source]
   */
  _applyStatus(target, statusId, duration, source = null) {
    if (this._statusEffects) {
      this._statusEffects.applyStatus(target, statusId, duration, source);
    } else {
      // Fallback: directly push to combatant's statusEffects array
      const statusData = dataStore.getById('status_effects', statusId);
      const effect = {
        id: statusId,
        name: statusData ? statusData.name : statusId,
        duration,
        effects: statusData ? statusData.effects : [],
        source: source ? source.id : null,
      };

      // Check for stacking
      const existing = target.statusEffects.find(e => e.id === statusId);
      if (existing && statusData && !statusData.stackable) {
        // Refresh duration
        existing.duration = Math.max(existing.duration, duration);
      } else {
        target.statusEffects.push(effect);
      }

      if (this._combatSystem) {
        this._combatSystem._addLog(`${target.name} is now ${effect.name}!`);
      }

      eventBus.emit('combat:status-apply', {
        target,
        statusId,
        duration,
        source,
      });
    }
  }

  /**
   * Possibly create a persistent tile effect based on the skill.
   * Fire skills create fire tiles, frost skills create ice, etc.
   * @param {object} caster
   * @param {object} skill
   * @param {{ x: number, y: number }} target
   */
  _maybeCreateTileEffect(caster, skill, target) {
    if (!this._tileEffects) return;

    // Only AoE or high-tier skills create tile effects
    const isAoe = skill.targetType === TARGET_TYPES.AOE_ENEMY || skill.targetType === TARGET_TYPES.AOE_ALLY;
    const isTier3Plus = skill.tier >= 3;

    if (!isAoe && !isTier3Plus) return;

    let tileType = null;
    let tileDuration = 2;

    if (skill.damageType === 'fire') {
      tileType = 'fire';
      tileDuration = 3;
    } else if (skill.damageType === 'frost') {
      tileType = 'ice';
      tileDuration = 3;
    } else if (skill.damageType === 'poison') {
      tileType = 'poison';
      tileDuration = 4;
    }

    if (tileType) {
      this._tileEffects.addEffect(target.x, target.y, tileType, tileDuration, caster);
    }
  }

  /**
   * Get attribute modifier: (value - 10) / 2, floored.
   * @param {number} value
   * @returns {number}
   */
  _getMod(value) {
    return Math.floor((value - 10) / 2);
  }
}

/** Singleton instance. */
const skillSystem = new SkillSystem();
export default skillSystem;
export { SkillSystem, TARGET_TYPES };
